import React, { useState } from "react";
import { toast } from "sonner";
import { X, Megaphone, Loader2, Copy, Linkedin, Twitter, Instagram, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const PLATFORMS = [
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
  { id: "twitter", label: "X / Twitter", icon: Twitter },
  { id: "instagram", label: "Instagram", icon: Instagram },
  { id: "email", label: "Email", icon: Mail },
];

export default function AIMarketingModal({ courses, onClose }) {
  const [courseId, setCourseId] = useState(courses[0]?.id || "");
  const [platform, setPlatform] = useState("linkedin");
  const [busy, setBusy] = useState(false);
  const [copy, setCopy] = useState("");

  const generate = async () => {
    setBusy(true);
    setCopy("");
    try {
      const { data } = await api.post(`/courses/${courseId}/ai-marketing`, { platform });
      setCopy(data.copy);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to generate marketing copy");
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(copy);
      toast.success("Copied to clipboard!");
    } catch (e) {
      toast.error("Could not copy — please copy manually");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="ai-marketing-modal">
      <div className="glass-panel rounded-2xl p-6 max-w-lg w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-white text-sm flex items-center gap-2"><Megaphone className="w-4 h-4 text-pink-300" /> AI Marketing</h3>
          <button onClick={onClose} data-testid="ai-marketing-close-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Course</label>
        <select value={courseId} onChange={(e) => setCourseId(e.target.value)} data-testid="ai-marketing-course-select" className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>

        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-3 block">Platform</label>
        <div className="grid grid-cols-4 gap-2 mt-1.5">
          {PLATFORMS.map((p) => (
            <button key={p.id} onClick={() => setPlatform(p.id)} data-testid={`ai-marketing-platform-${p.id}`}
              className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-[11px] font-semibold ${platform === p.id ? "border-pink-400 bg-pink-400/10 text-pink-300" : "border-white/10 text-slate-400"}`}>
              <p.icon className="w-4 h-4" /> {p.label}
            </button>
          ))}
        </div>

        <button onClick={generate} disabled={busy} data-testid="ai-marketing-generate-btn" className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />} Generate Copy
        </button>

        {copy && (
          <div className="mt-4 bg-white/[0.03] border border-white/10 rounded-xl p-4" data-testid="ai-marketing-result">
            <p className="text-sm text-slate-200 whitespace-pre-wrap">{copy}</p>
            <button onClick={copyToClipboard} data-testid="ai-marketing-copy-btn" className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-pink-300 hover:text-pink-200"><Copy className="w-3.5 h-3.5" /> Copy to clipboard</button>
          </div>
        )}
      </div>
    </div>
  );
}
